import React from 'react';
import {
  Box, Button, Chip, ToggleButton, ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  StargraveCrewData, StargraveMemberGameState, StargraveSelectedPower,
  StargraveAdvancedWeaponInstance, StargraveAlienArtefactInstance, StargraveStatLine, StargraveTechnologyInstance,
} from '../../types';
import { stargravePowers } from '../../data/stargrave/coreRules';
import { getAllStargraveEquipment, getAllStargraveSoldiers, useStargraveEquipment } from '../../stores/stargraveCustomStore';
import { stargraveAdvancedTechnology } from '../../data/stargrave/advancedTechnology';
import { stargraveAdvancedWeapons } from '../../data/stargrave/advancedWeapons';
import { stargraveAlienArtefacts } from '../../data/stargrave/alienArtefacts';
import { stargravePermanentInjuries } from '../../data/stargrave/permanentInjuries';
import { isStargraveWounded, applyStargraveLootPenalties, getStargraveLootStatModifiers } from '../../game/stargravePlayRules';
import { ItemDetailsPopover } from '../shared/ItemDetailsPopover';
import { PopoverState } from '../../hooks/useItemPopover';
import { getEffectiveLeaderStats } from '../../game/stargraveCampaignRules';
import { applyStargraveGearModifiers, getStargraveGearStatModifiers } from '../../game/stargraveLeaderRules';
import { applyStargraveTechnologyModifiers, getStargraveTechnologyEffects, getStargraveTechnologyStatModifiers } from '../../game/stargraveTechRules';
import { UseTechnologyDialog } from '../shared/UseTechnologyDialog';
import { SharedMemberStatsAndHealth, buildStargraveSharedStats, buildStargraveMiniStats } from '../shared/SharedMemberStatsAndHealth';
import { MemberCard } from '../shared/MemberCard';
import { useAuthStore } from '../../stores/authStore';
import { MemberTraitItem } from '../shared/MemberTraits';
import { MemberStateToggles } from '../shared/MemberStateToggles';
import { MemberMiniCard } from '../shared/MemberMiniCard';
import { miniFlexSx } from '../../App';
import { getStargraveSoldierTraits, applyStargraveRewireModifiers, getStargraveRewireStatModifiers } from '../../game/stargraveSoldierRules';
import { applyStargraveBuffsToStats, formatStargraveBuffEffect, getStargraveBuffStatModifiers, getStargraveBuffById } from '../../game/stargraveBuffRules';
import { StargraveManageBuffsModal } from '../modals/StargraveManageBuffsModal';
import { ConfirmActionDialog } from '../shared/ConfirmActionDialog';
import { generateBloodSpatterSVG, hashStringToSeed } from '../../theme/surfaces';
import { isFieldedStargraveSoldier } from '../../game/stargraveUpkeepRules';

interface PlayMember {
  id: string;
  name: string;
  role: string;
  maximumHealth: number;
  isRobot: boolean;
  stats: StargraveStatLine;
  baseStats: StargraveStatLine;
  injuryIds: string[];
  equipmentIds: string[];
  technology: StargraveTechnologyInstance[];
  advancedWeapons: StargraveAdvancedWeaponInstance[];
  alienArtefacts: StargraveAlienArtefactInstance[];
  gameState: StargraveMemberGameState;
  powers: StargraveSelectedPower[];
  traits: MemberTraitItem[];
  notes?: string;
  isTemporary?: boolean;
  rewiredStat?: 'move' | 'fight' | 'armour';
  /** Gear whose stat modifiers are not already baked into the stat line (leaders only). */
  gearModifierIds: string[];
}

interface StargravePlayTabProps {
  warbandId: string;
  crewData: StargraveCrewData;
  minified?: boolean;
  onDamage: (memberId: string, amount: number) => void;
  onHeal: (memberId: string, amount: number) => void;
  onStateChange: (memberId: string, changes: Partial<StargraveMemberGameState>) => void;
  onAddBuff: (memberId: string, buffId: string) => void;
  onRemoveBuff: (memberId: string, buffId: string) => void;
  onUseTechnology: (instanceId: string, options?: { healTargetId?: string; healAmount?: number }) => void;
  onUseArtefact: (instanceId: string) => void;
  onEndGame: () => void;
}

const PlayMemberCard: React.FC<{
  warbandId: string;
  accessToken: string | null;
  member: PlayMember;
  minified?: boolean;
  onDamage: (amount: number) => void;
  onHeal: (amount: number) => void;
  onStateChange: (changes: Partial<StargraveMemberGameState>) => void;
  onAddBuff: (buffId: string) => void;
  onRemoveBuff: (buffId: string) => void;
  onPowerClick: (event: React.MouseEvent<HTMLElement>, power: StargraveSelectedPower) => void;
  onEquipmentClick: (event: React.MouseEvent<HTMLElement>, equipmentId: string) => void;
  onTechnologyClick: (event: React.MouseEvent<HTMLElement>, technology: StargraveTechnologyInstance) => void;
  onAdvancedWeaponClick: (event: React.MouseEvent<HTMLElement>, weapon: StargraveAdvancedWeaponInstance) => void;
  onAlienArtefactClick: (event: React.MouseEvent<HTMLElement>, artefact: StargraveAlienArtefactInstance) => void;
}> = ({ warbandId, accessToken, member, minified = false, onDamage, onHeal, onStateChange, onAddBuff, onRemoveBuff, onPowerClick, onEquipmentClick, onTechnologyClick, onAdvancedWeaponClick, onAlienArtefactClick }) => {
  const stargraveEquipment = useStargraveEquipment();
  const [manageBuffsOpen, setManageBuffsOpen] = React.useState(false);
  const [buffPendingDelete, setBuffPendingDelete] = React.useState<string | null>(null);
  const { gameState } = member;
  const wounded = isStargraveWounded(gameState, member.maximumHealth, member.isRobot);
  const outOfGame = gameState.currentHealth <= 0 || gameState.leftTable;
  const incapacitated = gameState.currentHealth <= 0;
  const bloodSpatterUrl = React.useMemo(() => generateBloodSpatterSVG(hashStringToSeed(member.id)), [member.id]);
  const bloodOverlay = incapacitated
    ? <Box aria-hidden style={{ position: 'absolute', inset: 0, zIndex: 2 }}
        sx={{ borderRadius: 'inherit', pointerEvents: 'none', backgroundImage: bloodSpatterUrl, backgroundRepeat: 'no-repeat', backgroundSize: 'cover', backgroundPosition: 'center' }} />
    : undefined;
  const gearStats = applyStargraveGearModifiers(member.stats, member.gearModifierIds);
  const techStats = applyStargraveTechnologyModifiers(gearStats, member.technology, member.id);
  const buffedStats = applyStargraveBuffsToStats(techStats, gameState.activeBuffs);
  const effectiveStats = applyStargraveLootPenalties(buffedStats, gameState.loot);
  const sharedStats = buildStargraveSharedStats(member.baseStats, effectiveStats, (stat) => [
    ...member.injuryIds.flatMap((injuryId) => {
      const injury = stargravePermanentInjuries.find((candidate) => candidate.id === injuryId);
      const modifier = injury?.statModifiers?.[stat];
      return !injury || modifier === undefined ? [] : [{ label: injury.name, value: modifier, type: 'add' as const }];
    }),
    ...getStargraveRewireStatModifiers(member.rewiredStat, stat),
    ...getStargraveGearStatModifiers(member.gearModifierIds, stat),
    ...getStargraveTechnologyStatModifiers(member.technology, member.id, stat),
    ...getStargraveBuffStatModifiers(gameState.activeBuffs, stat),
    ...getStargraveLootStatModifiers(buffedStats, gameState.loot, stat),
  ]);

  const health = {
    current: gameState.currentHealth,
    max: member.maximumHealth,
    onChange: (next: number) => {
      const delta = next - gameState.currentHealth;
      if (delta < 0) onDamage(Math.abs(delta));
      if (delta > 0) onHeal(delta);
    },
  };

  const card = <MemberCard
    warbandId={warbandId}
    memberId={member.id}
    name={member.name}
    subtitle={member.role}
    accessToken={accessToken}
    statusContent={<>
      {member.isTemporary && <Chip size="small" label="Temporary" />}
      {wounded && <Chip size="small" color="warning" label="Wounded: 1 action, -2 rolls" />}
      {gameState.leftTable && <Chip size="small" color="warning" label="Left Table" />}
      {outOfGame && <Chip size="small" color="error" label="Out of Game" />}
    </>}
    actionMenuItems={[
      { label: 'Leave Table', checked: gameState.leftTable, onClick: () => onStateChange({ leftTable: !gameState.leftTable }) },
    ]}
    statsContent={<SharedMemberStatsAndHealth stats={sharedStats} />}
    health={health}
    opacity={outOfGame ? 0.7 : 1}
    overlay={bloodOverlay}
    injuries={member.injuryIds.flatMap((injuryId) => {
      const injury = stargravePermanentInjuries.find((candidate) => candidate.id === injuryId);
      return injury ? [{ id: injury.id, name: injury.name, description: injury.summary }] : [];
    })}
    traits={member.traits}
    effects={[
      ...gameState.activeBuffs.flatMap((active) => {
        const buff = getStargraveBuffById(active.buffId);
        return buff ? [{
          id: buff.id,
          name: active.stackCount > 1 ? `${buff.name} ×${active.stackCount}` : buff.name,
          description: buff.summary,
          modifiers: formatStargraveBuffEffect(buff),
          onDelete: () => setBuffPendingDelete(buff.id),
        }] : [];
      }),
      ...getStargraveTechnologyEffects(member.technology, member.id),
    ]}
    onAddEffect={() => setManageBuffsOpen(true)}
    addEffectDisabled={outOfGame}
    footerContent={<>
      <MemberStateToggles sx={{ mr: 'auto', mb: 0 }} toggles={[
        { id: 'stunned', label: 'Stunned', selected: gameState.stunned, onToggle: () => onStateChange({ stunned: !gameState.stunned }) },
        { id: 'poisoned', label: 'Poisoned', selected: gameState.poisoned, onToggle: () => onStateChange({ poisoned: !gameState.poisoned }) },
      ]} />
      {gameState.loot === 'physical' && <Typography variant="caption" color="text.secondary">
        Physical loot: half Move, -1 Fight, -1 Shoot.
      </Typography>}
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Loot</Typography>
      <ToggleButtonGroup size="small" exclusive value={gameState.loot ?? 'none'}
        onChange={(_, value) => value && onStateChange({ loot: value === 'none' ? null : value })}
        aria-label={`${member.name} loot`}>
        <ToggleButton value="none">None</ToggleButton>
        <ToggleButton value="data">Data</ToggleButton>
        <ToggleButton value="physical">Physical</ToggleButton>
      </ToggleButtonGroup>
    </>}
    notes={member.notes}
    lowerContent={<>
    <StargraveManageBuffsModal
      open={manageBuffsOpen}
      memberName={member.name}
      activeBuffs={gameState.activeBuffs}
      onClose={() => setManageBuffsOpen(false)}
      onAdd={onAddBuff}
    />
    <ConfirmActionDialog
      open={buffPendingDelete !== null}
      title="Remove buff / debuff"
      message={buffPendingDelete ? `Remove "${getStargraveBuffById(buffPendingDelete)?.name}" from ${member.name}?` : ''}
      confirmLabel="Remove"
      confirmColor="error"
      onClose={() => setBuffPendingDelete(null)}
      onConfirm={() => {
        if (buffPendingDelete) onRemoveBuff(buffPendingDelete);
        setBuffPendingDelete(null);
      }}
    />

    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 700 }}>Equipment</Typography>
      {(member.equipmentIds.length + member.technology.length + member.advancedWeapons.length + member.alienArtefacts.length) > 0 ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {member.equipmentIds.map((id) => {
            const item = stargraveEquipment.find((candidate) => candidate.id === id);
            return <Chip key={id} size="small" clickable label={item?.name ?? id}
              onClick={(event) => onEquipmentClick(event, id)} />;
          })}
          {member.technology.map((instance) => {
            const technology = stargraveAdvancedTechnology.find((candidate) => candidate.id === instance.technologyId);
            return technology ? <Chip key={instance.instanceId} size="small" clickable label={technology.name}
              onClick={(event) => onTechnologyClick(event, instance)} /> : null;
          })}
          {member.advancedWeapons.map((instance) => {
            const weapon = stargraveAdvancedWeapons.find((candidate) => candidate.id === instance.weaponId);
            return weapon ? <Chip key={instance.instanceId} size="small" clickable label={weapon.name}
              onClick={(event) => onAdvancedWeaponClick(event, instance)} /> : null;
          })}
          {member.alienArtefacts.map((instance) => {
            const artefact = stargraveAlienArtefacts.find((candidate) => candidate.id === instance.artefactId);
            return artefact ? <Chip key={instance.instanceId} size="small" clickable label={artefact.name}
              onClick={(event) => onAlienArtefactClick(event, instance)} /> : null;
          })}
        </Box>
      ) : <Typography variant="body2" color="text.secondary">None</Typography>}
    </Box>

    {member.powers.length > 0 && <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 700 }}>Powers</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {member.powers.map((selection) => <Chip key={selection.powerId} size="small" clickable
          label={`${stargravePowers.find((power) => power.id === selection.powerId)?.name ?? selection.powerId} ${selection.activation} (${selection.strain})`}
          onClick={(event) => onPowerClick(event, selection)} />)}
      </Box>
    </Box>}
    </>}
  />;

  if (!minified) return card;
  return <MemberMiniCard
    warbandId={warbandId}
    memberId={member.id}
    name={member.name}
    subtitle={member.role}
    accessToken={accessToken}
    health={health}
    stats={buildStargraveMiniStats(effectiveStats)}
    statusContent={gameState.loot
      ? <Chip size="small" label={gameState.loot === 'physical' ? 'Loot: P' : 'Loot: D'} title={`Carrying ${gameState.loot} loot`} />
      : undefined}
    opacity={outOfGame ? 0.7 : 1}
    overlay={bloodOverlay}
    fullCard={card}
  />;
};

export const StargravePlayTab: React.FC<StargravePlayTabProps> = ({
  warbandId,
  crewData,
  minified = false,
  onDamage,
  onHeal,
  onStateChange,
  onAddBuff,
  onRemoveBuff,
  onUseTechnology,
  onUseArtefact,
  onEndGame,
}) => {
  const [detailsPopover, setDetailsPopover] = React.useState<PopoverState | null>(null);
  const [popoverTechnology, setPopoverTechnology] = React.useState<StargraveTechnologyInstance | null>(null);
  const [popoverArtefact, setPopoverArtefact] = React.useState<StargraveAlienArtefactInstance | null>(null);
  const [useTechnologyTarget, setUseTechnologyTarget] = React.useState<StargraveTechnologyInstance | null>(null);
  const [confirmEndGameOpen, setConfirmEndGameOpen] = React.useState(false);
  const accessToken = useAuthStore((state) => state.accessToken);
  const captainStats = crewData.captain ? getEffectiveLeaderStats(crewData.captain) : null;
  const firstMateStats = crewData.firstMate ? getEffectiveLeaderStats(crewData.firstMate) : null;
  const openPowerPopover = (event: React.MouseEvent<HTMLElement>, selection: StargraveSelectedPower) => {
    const power = stargravePowers.find((candidate) => candidate.id === selection.powerId);
    if (!power) return;
    setDetailsPopover({
      anchor: event.currentTarget,
      id: power.id,
      itemType: 'spell',
      name: power.name,
      description: power.summary,
      tags: [],
      modifiers: `Activation ${selection.activation} | Strain ${selection.strain} | ${power.timing}`,
    });
  };
  const openEquipmentPopover = (event: React.MouseEvent<HTMLElement>, equipmentId: string) => {
    const equipment = getAllStargraveEquipment().find((candidate) => candidate.id === equipmentId);
    if (!equipment) return;
    const modifiers = [
      `${equipment.gearSlots} gear slot${equipment.gearSlots === 1 ? '' : 's'}`,
      equipment.damageModifier !== undefined ? `Damage ${equipment.damageModifier >= 0 ? '+' : ''}${equipment.damageModifier}` : null,
      equipment.maxRange !== undefined ? `Range ${equipment.maxRange}\"` : null,
      equipment.armourModifier !== undefined ? `Armour ${equipment.armourModifier >= 0 ? '+' : ''}${equipment.armourModifier}` : null,
      equipment.moveModifier !== undefined ? `Move ${equipment.moveModifier}` : null,
    ].filter(Boolean).join(' | ');
    setDetailsPopover({
      anchor: event.currentTarget,
      id: equipment.id,
      itemType: equipment.category === 'weapon' ? 'weapon' : undefined,
      name: equipment.name,
      description: equipment.notes ?? null,
      tags: [],
      modifiers,
    });
  };
  const openTechnologyPopover = (event: React.MouseEvent<HTMLElement>, instance: StargraveTechnologyInstance) => {
    const technology = stargraveAdvancedTechnology.find((candidate) => candidate.id === instance.technologyId);
    if (!technology) return;
    setPopoverTechnology(instance);
    setPopoverArtefact(null);
    setDetailsPopover({
      anchor: event.currentTarget,
      id: technology.id,
      name: technology.name,
      description: technology.summary,
      tags: [],
      modifiers: `${technology.gearSlots} gear slot${technology.gearSlots === 1 ? '' : 's'}${instance.remainingUses !== undefined ? ` | ${instance.remainingUses} uses remaining` : ''}`,
    });
  };
  const openAdvancedWeaponPopover = (event: React.MouseEvent<HTMLElement>, instance: StargraveAdvancedWeaponInstance) => {
    const weapon = stargraveAdvancedWeapons.find((candidate) => candidate.id === instance.weaponId);
    if (!weapon) return;
    setDetailsPopover({
      anchor: event.currentTarget,
      id: weapon.id,
      itemType: 'weapon',
      name: weapon.name,
      description: weapon.summary,
      tags: [],
      modifiers: `${weapon.gearSlots} gear slot${weapon.gearSlots === 1 ? '' : 's'} | Indestructible`,
    });
  };
  const openAlienArtefactPopover = (event: React.MouseEvent<HTMLElement>, instance: StargraveAlienArtefactInstance) => {
    const artefact = stargraveAlienArtefacts.find((candidate) => candidate.id === instance.artefactId);
    if (!artefact) return;
    setPopoverArtefact(artefact.uses !== undefined ? instance : null);
    setPopoverTechnology(null);
    const remaining = artefact.uses !== undefined ? (instance.remainingUses ?? artefact.uses) : undefined;
    setDetailsPopover({
      anchor: event.currentTarget,
      id: artefact.id,
      name: artefact.name,
      description: artefact.summary,
      tags: [],
      modifiers: `1 gear slot | Captain or First Mate only${remaining !== undefined ? ` | ${remaining} use${remaining === 1 ? '' : 's'} remaining` : ''}`,
    });
  };
  const visibleEquipmentIds = (ownerId: string, equipmentIds: string[]) => {
    const replaced = new Set(crewData.advancedWeapons.filter((item) => item.ownerId === ownerId)
      .map((item) => stargraveAdvancedWeapons.find((weapon) => weapon.id === item.weaponId)?.baseWeaponId)
      .filter((id): id is string => Boolean(id)));
    return equipmentIds.filter((id) => !replaced.has(id));
  };
  const members: PlayMember[] = [
    ...(crewData.captain ? [{
      id: crewData.captain.id,
      name: crewData.captain.name,
      role: 'Captain',
      maximumHealth: captainStats!.health,
      isRobot: false,
      stats: captainStats!,
      baseStats: crewData.captain.stats,
      injuryIds: crewData.captain.permanentInjuryIds,
      equipmentIds: visibleEquipmentIds(crewData.captain.id, crewData.captain.gearIds),
      technology: crewData.advancedTechnology.filter((item) => item.ownerId === crewData.captain?.id),
      advancedWeapons: crewData.advancedWeapons.filter((item) => item.ownerId === crewData.captain?.id),
      alienArtefacts: crewData.alienArtefacts.filter((item) => item.ownerId === crewData.captain?.id),
      gameState: crewData.captain.gameState,
      powers: crewData.captain.powers,
      traits: [],
      notes: crewData.captain.notes,
      gearModifierIds: visibleEquipmentIds(crewData.captain.id, crewData.captain.gearIds),
    }] : []),
    ...(crewData.firstMate ? [{
      id: crewData.firstMate.id,
      name: crewData.firstMate.name,
      role: 'First Mate',
      maximumHealth: firstMateStats!.health,
      isRobot: false,
      stats: firstMateStats!,
      baseStats: crewData.firstMate.stats,
      injuryIds: crewData.firstMate.permanentInjuryIds,
      equipmentIds: visibleEquipmentIds(crewData.firstMate.id, crewData.firstMate.gearIds),
      technology: crewData.advancedTechnology.filter((item) => item.ownerId === crewData.firstMate?.id),
      advancedWeapons: crewData.advancedWeapons.filter((item) => item.ownerId === crewData.firstMate?.id),
      alienArtefacts: crewData.alienArtefacts.filter((item) => item.ownerId === crewData.firstMate?.id),
      gameState: crewData.firstMate.gameState,
      powers: crewData.firstMate.powers,
      traits: [],
      notes: crewData.firstMate.notes,
      gearModifierIds: visibleEquipmentIds(crewData.firstMate.id, crewData.firstMate.gearIds),
    }] : []),
    ...crewData.soldiers.filter(isFieldedStargraveSoldier).map((soldier) => {
      const profile = getAllStargraveSoldiers().find((candidate) => candidate.id === soldier.profileId);
      const profileStats = profile ?? { move: 0, fight: 0, shoot: 0, armour: 0, will: 0, health: soldier.gameState.currentHealth };
      return {
        id: soldier.id,
        name: soldier.name,
        role: profile?.name ?? 'Soldier',
        maximumHealth: profile?.health ?? soldier.gameState.currentHealth,
        isRobot: soldier.isRobot,
        stats: applyStargraveRewireModifiers(profileStats, soldier.rewiredStat),
        baseStats: profileStats,
        injuryIds: [],
        equipmentIds: visibleEquipmentIds(soldier.id, profile?.equipmentIds ?? []),
        technology: crewData.advancedTechnology.filter((item) => item.ownerId === soldier.id),
        advancedWeapons: crewData.advancedWeapons.filter((item) => item.ownerId === soldier.id),
        alienArtefacts: [],
        gameState: soldier.gameState,
        powers: [],
        traits: getStargraveSoldierTraits(soldier.isRobot, profile, soldier.rewiredStat),
        notes: soldier.notes,
        isTemporary: soldier.isTemporary,
        rewiredStat: soldier.rewiredStat,
        // Soldier profile stat lines already include their standard equipment.
        gearModifierIds: [],
      };
    }),
  ];

  const isOutOfGame = (member: PlayMember) =>
    member.gameState.currentHealth <= 0 || member.gameState.leftTable;
  const activeMembers = members.filter((member) => !isOutOfGame(member));
  const outOfGameMembers = members.filter(isOutOfGame)
    .sort((left, right) => left.name.localeCompare(right.name));
  const memberGridSx = minified
    ? miniFlexSx
    : { display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 2 };
  const renderMember = (member: PlayMember) => <PlayMemberCard key={member.id} member={member}
    warbandId={warbandId}
    accessToken={accessToken}
    minified={minified}
    onDamage={(amount) => onDamage(member.id, amount)}
    onHeal={(amount) => onHeal(member.id, amount)}
    onStateChange={(changes) => onStateChange(member.id, changes)}
    onAddBuff={(buffId) => onAddBuff(member.id, buffId)}
    onRemoveBuff={(buffId) => onRemoveBuff(member.id, buffId)}
    onPowerClick={openPowerPopover}
    onEquipmentClick={openEquipmentPopover}
    onTechnologyClick={openTechnologyPopover}
    onAdvancedWeaponClick={openAdvancedWeaponPopover}
    onAlienArtefactClick={openAlienArtefactPopover} />;

  return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1 }}>
      <Button variant="contained" onClick={() => setConfirmEndGameOpen(true)}>End Game</Button>
    </Box>
    <ConfirmActionDialog
      open={confirmEndGameOpen}
      title="End game"
      message="End the game and move to the post-game sequence?"
      confirmLabel="End Game"
      onClose={() => setConfirmEndGameOpen(false)}
      onConfirm={() => {
        setConfirmEndGameOpen(false);
        onEndGame();
      }}
    />
    {members.length === 0 ? <Typography color="text.secondary">No crew members available.</Typography> : (
      <>
        <Box sx={memberGridSx}>
          {activeMembers.map(renderMember)}
        </Box>
        {outOfGameMembers.length > 0 && <>
          <Typography variant="h6">Out of the game</Typography>
          <Box sx={memberGridSx}>
            {outOfGameMembers.map(renderMember)}
          </Box>
        </>}
      </>
    )}
    <ItemDetailsPopover
      popover={detailsPopover}
      onClose={() => { setDetailsPopover(null); setPopoverTechnology(null); setPopoverArtefact(null); }}
      showUseAction={Boolean(popoverArtefact) || Boolean(popoverTechnology && (() => {
        const technology = stargraveAdvancedTechnology.find((candidate) => candidate.id === popoverTechnology.technologyId);
        return technology && (technology.useBuffId || technology.healAmount !== undefined || popoverTechnology.remainingUses !== undefined);
      })())}
      useDisabled={
        (popoverTechnology?.remainingUses !== undefined && popoverTechnology.remainingUses <= 0) ||
        (popoverArtefact !== null && (popoverArtefact.remainingUses ?? 1) <= 0)
      }
      onUse={() => {
        if (popoverArtefact) {
          onUseArtefact(popoverArtefact.instanceId);
        } else if (popoverTechnology) {
          setUseTechnologyTarget(popoverTechnology);
        }
        setDetailsPopover(null);
        setPopoverTechnology(null);
        setPopoverArtefact(null);
      }}
    />
    {useTechnologyTarget && (() => {
      const technology = stargraveAdvancedTechnology.find((candidate) => candidate.id === useTechnologyTarget.technologyId);
      return technology ? <UseTechnologyDialog
        instance={useTechnologyTarget}
        technology={technology}
        members={members.map((member) => ({ id: member.id, name: member.name }))}
        onClose={() => setUseTechnologyTarget(null)}
        onConfirm={(options) => {
          onUseTechnology(useTechnologyTarget.instanceId, options);
          setUseTechnologyTarget(null);
        }}
      /> : null;
    })()}
  </Box>;
};
